import cx from 'classnames';
import * as React from 'react';
import { FormGroup } from '../FormGroup/FormGroup';
import { HelpBlock } from '../HelpBlock/HelpBlock';
import { noop } from '../helper';
import { callAll } from '../helper/fp';
import { useId } from '../helper/hooks';
import { Label } from '../Label/Label';

export type CheckboxFieldProps = {
  /** label to displayed next to checkbox */
  label: React.ReactNode;
  /** id for the checkbox input, which willl be used to set the label as well */
  id?: string;
  /**
   * callback that will be invoked when value is changed. Convenient props so you do not
   * need to get the value from onChange `event.target.checked`.
   */
  onChangeValue?: (checked: boolean) => void;
  /** className to be added to form group container */
  wrapperClassName?: string;
  /** validation status of the field */
  status?: 'success' | 'error' | 'warning';
  /** max width of the field */
  maxWidth?: 'narrow' | 'small' | 'wide' | 'wide-thin';
  /** state if the field is compact, i.e. smaller spacing between elements */
  compact?: boolean;
  /** help text for the field */
  helpText?: React.ReactNode;
} & JSX.IntrinsicElements['input'];
/**
 * `CheckboxField` is a control that consists of a checkbox and a label
 */
export const CheckboxField: React.FC<CheckboxFieldProps> = (props) => {
  const {
    label,
    id: providedId,
    wrapperClassName,
    onChange,
    status,
    maxWidth,
    compact,
    helpText,
    onChangeValue = noop,
    className,
    'aria-checked': ariaChecked,
    ...inputProps
  } = props;

  const id = useId(providedId);

  return (
    <FormGroup className={wrapperClassName} status={status} maxWidth={maxWidth} compact={compact}>
      <input
        id={id}
        onChange={callAll(onChange, (ev) => onChangeValue(ev.target.checked))}
        className={cx('visuallyhidden', className)}
        aria-checked={ariaChecked || inputProps.checked}
        {...inputProps}
        type="checkbox"
      />
      <Label
        htmlFor={id}
        className={cx('checkbox', inputProps.required && 'checkbox--is-required', status && `has-${status}`)}
      >
        <span className="checkbox-label">{label}</span>
      </Label>
      {helpText && <HelpBlock>{helpText}</HelpBlock>}
    </FormGroup>
  );
};

export default CheckboxField;
